// src/components/routes/RouteForm.jsx
import React, { useEffect, useState } from "react";
import {
    Form, FormGroup, Label, Input, Row, Col, Button, FormFeedback
} from "reactstrap";
import Session from "../auth/Session";

const TransactionTypeForm = ({ transactionTypeItem, apiBaseUrl, onSuccess, onCancel }) => {
    const [errors, setErrors] = useState({});
    // QBO checking categories (expense accounts) available to link this type's QbBillItemId to.
    const [checkingItems, setCheckingItems] = useState([]);
    const [checkingItemsError, setCheckingItemsError] = useState("");

    const [formData, setFormData] = useState(transactionTypeItem || {
        id: 0,
        transactionName: "",
        quickBooksType: "",
        isPositiveAdjustment: true,
        isActive: true,
        qbBillItemId: "",
    });

    useEffect(() => {
        if (transactionTypeItem) {
            setFormData({
                id: transactionTypeItem.id,
                transactionName: transactionTypeItem.transactionName ?? "",
                quickBooksType: transactionTypeItem.quickBooksType ?? "",
                isPositiveAdjustment: transactionTypeItem.isPositiveAdjustment ?? true,
                isActive: transactionTypeItem.isActive ?? true,
                // Round-trip the current QBO checking-category link so saving preserves it.
                qbBillItemId: transactionTypeItem.qbBillItemId ?? ""
            });
        }
    }, [transactionTypeItem]);

    // Load the QBO checking categories for the dropdown. Requires a connected QuickBooks; if the call
    // fails the field still preserves any existing link.
    useEffect(() => {
        (async () => {
            try {
                const token = await Session.getToken();
                const res = await fetch("/api/qbo/checking-items", {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (!res.ok) throw new Error(`${res.status}`);
                setCheckingItems(await res.json());
            } catch (e) {
                setCheckingItemsError("Could not load QuickBooks checking categories (QuickBooks may not be connected). Any existing link is preserved.");
                setCheckingItems([]);
            }
        })();
    }, []);

    const validate = () => {
        const v = {};
        if (!formData.transactionName) {
            v.transactionName = "Transaction Type name is required";
        } else {
        }

        if (!formData.quickBooksType) v.quickBooksType = "QuickBooks Type is required";

        setErrors(v);
        return Object.keys(v).length === 0;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(p => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const method = transactionTypeItem ? "PUT" : "POST";
        const url = transactionTypeItem
            ? `${apiBaseUrl}/api/transactiontype/${formData.id}`
            : `${apiBaseUrl}/api/transactiontype`;

        let data = {
            ...formData
        };

        const token = await Session.getToken();
        const response = await fetch(url, {
            method,
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify(data),
        });

        if (response.ok) onSuccess();
    };

    return (
        <Form onSubmit={handleSubmit}>
            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label>Type Name</Label>
                        <Input name="transactionName" value={formData.transactionName} onChange={handleChange} invalid={!!errors.transactionName} />
                        <FormFeedback>{errors.transactionName}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label>QuickBooks Type</Label>
                        <Input name="quickBooksType" value={formData.quickBooksType} onChange={handleChange} />
                    </FormGroup>
                </Col>
                <Col md={12}>
                    <FormGroup>
                        <Label>QuickBooks Checking Category</Label>
                        <Input
                            name="qbBillItemId"
                            type="select"
                            value={formData.qbBillItemId ?? ""}
                            onChange={handleChange}
                        >
                            <option value="">-- Not linked --</option>
                            {/* Preserve a current link even if it isn't in the loaded list (e.g. QBO offline). */}
                            {formData.qbBillItemId
                                && !checkingItems.some(c => `${c.id}` === `${formData.qbBillItemId}`) && (
                                <option value={formData.qbBillItemId}>Currently linked (id {formData.qbBillItemId})</option>
                            )}
                            {checkingItems.map(c => (
                                <option key={c.id} value={c.id}>{c.name}{c.accountType ? ` (${c.accountType})` : ""}</option>
                            ))}
                        </Input>
                        <small className="text-muted">
                            The QuickBooks account used for this type on paysheet checks. Required for check lines to post.
                        </small>
                        {checkingItemsError && <div className="text-warning small mt-1">{checkingItemsError}</div>}
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup check className="mb-3">
                        <Label check>
                            <Input
                                name="isActive"
                                type="checkbox"
                                checked={formData.isActive}
                                onChange={handleChange}
                            />{" "}
                            Active
                        </Label>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <FormGroup check className="mb-3">
                        <Label check>
                            <Input
                                name="isPositiveAdjustment"
                                type="checkbox"
                                checked={formData.isPositiveAdjustment}
                                onChange={handleChange}
                            />{" "}
                            Positive adjustment
                        </Label>
                    </FormGroup>
                </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2">
                <Button color="secondary" type="button" onClick={onCancel}>Cancel</Button>
                <Button color="primary" type="submit">{`${formData.id}` !== `0` ? "Update" : "Create"}</Button>
            </div>
        </Form>
    );
};

export default TransactionTypeForm;
